import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent, CardHeader, Input, Button } from '../components/ui'
import { PageHeader } from '../../ui'
import { listVerifications, listShipmentVerifications, getVerificationPdfUrl } from '../lib/ixverifyApi'
import { useIXVerifyAuth } from '../contexts'
import type { Verification, ShipmentVerification } from '../types'

// Extended type with joined user info
type VerificationWithUser = Verification & {
  user: { full_name: string | null; email: string } | null
}

type ShipmentVerificationWithUser = ShipmentVerification & {
  user: { full_name: string | null; email: string } | null
}

type HistoryTab = 'verification' | 'shipment'
type DateFilter = 'all' | 'today' | 'week' | 'month' | 'custom'
type ResultFilter = 'all' | 'match' | 'mismatch'
type ShipmentResultFilter = 'all' | 'completed' | 'in_progress'

interface VerificationDetailModal {
  isOpen: boolean
  verification: VerificationWithUser | null
}

interface ShipmentDetailModal {
  isOpen: boolean
  shipment: ShipmentVerificationWithUser | null
}

export function VerificationHistory() {
  const { user, loading: authLoading } = useIXVerifyAuth()
  const [activeTab, setActiveTab] = useState<HistoryTab>('verification')
  const [verifications, setVerifications] = useState<VerificationWithUser[]>([])
  const [shipments, setShipments] = useState<ShipmentVerificationWithUser[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [resultFilter, setResultFilter] = useState<ResultFilter>('all')
  const [shipmentResultFilter, setShipmentResultFilter] = useState<ShipmentResultFilter>('all')
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [modal, setModal] = useState<VerificationDetailModal>({ isOpen: false, verification: null })
  const [shipmentModal, setShipmentModal] = useState<ShipmentDetailModal>({ isOpen: false, shipment: null })


  useEffect(() => {
    let cancelled = false

    const fetchData = async () => {
      if (!user) {
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        // Fetch verifications + shipment verifications (both with performer info).
        const siteId = user.site_id || undefined
        const [verificationResult, shipmentResult] = await Promise.all([
          listVerifications({ siteId, includeUser: true }),
          listShipmentVerifications({ siteId })
        ])

        if (cancelled) {
          return
        }

        if (verificationResult.error) throw verificationResult.error
        if (shipmentResult.error) {
          // Table might not exist yet, just log and continue
          console.warn('[VerificationHistory] Shipment table error:', shipmentResult.error)
        }

        setVerifications(verificationResult.data || [])
        setShipments(shipmentResult.data || [])
      } catch (error) {
        console.error('[VerificationHistory] Error fetching data:', error)
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    // Always return cleanup function to properly handle unmount
    if (authLoading) {
      return () => {
        cancelled = true
      }
    }

    if (user?.id) {
      fetchData()
    } else {
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [user?.id, user?.site_id, authLoading])

  const filteredVerifications = useMemo(() => {
    let filtered = verifications

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (v) =>
          v.ixrs_number.toLowerCase().includes(query) ||
          v.vial_number.toLowerCase().includes(query) ||
          v.verification_id?.toLowerCase().includes(query) ||
          v.user?.full_name?.toLowerCase().includes(query) ||
          v.user?.email?.toLowerCase().includes(query)
      )
    }

    // Result filter
    if (resultFilter !== 'all') {
      filtered = filtered.filter((v) => v.match_result === resultFilter)
    }

    // Date filter
    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (dateFilter) {
      case 'today':
        filtered = filtered.filter((v) => new Date(v.created_at) >= startOfToday)
        break
      case 'week': {
        const weekAgo = new Date(startOfToday)
        weekAgo.setDate(weekAgo.getDate() - 7)
        filtered = filtered.filter((v) => new Date(v.created_at) >= weekAgo)
        break
      }
      case 'month': {
        const monthAgo = new Date(startOfToday)
        monthAgo.setMonth(monthAgo.getMonth() - 1)
        filtered = filtered.filter((v) => new Date(v.created_at) >= monthAgo)
        break
      }
      case 'custom':
        if (customStartDate) {
          filtered = filtered.filter((v) => new Date(v.created_at) >= new Date(customStartDate))
        }
        if (customEndDate) {
          const endDate = new Date(customEndDate)
          endDate.setHours(23, 59, 59, 999)
          filtered = filtered.filter((v) => new Date(v.created_at) <= endDate)
        }
        break
    }

    return filtered
  }, [verifications, searchQuery, dateFilter, resultFilter, customStartDate, customEndDate])

  const filteredShipments = useMemo(() => {
    let filtered = shipments

    // Search filter - search in manifest IDs and user info
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (s) =>
          s.manifest_ids.some(id => id.toLowerCase().includes(query)) ||
          s.matched_ids.some(id => id.toLowerCase().includes(query)) ||
          s.user?.full_name?.toLowerCase().includes(query) ||
          s.user?.email?.toLowerCase().includes(query)
      )
    }

    // Status filter
    if (shipmentResultFilter !== 'all') {
      filtered = filtered.filter((s) => s.status === shipmentResultFilter)
    }

    // Date filter
    const now = new Date()
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    switch (dateFilter) {
      case 'today':
        filtered = filtered.filter((s) => new Date(s.created_at) >= startOfToday)
        break
      case 'week': {
        const weekAgo = new Date(startOfToday)
        weekAgo.setDate(weekAgo.getDate() - 7)
        filtered = filtered.filter((s) => new Date(s.created_at) >= weekAgo)
        break
      }
      case 'month': {
        const monthAgo = new Date(startOfToday)
        monthAgo.setMonth(monthAgo.getMonth() - 1)
        filtered = filtered.filter((s) => new Date(s.created_at) >= monthAgo)
        break
      }
      case 'custom':
        if (customStartDate) {
          filtered = filtered.filter((s) => new Date(s.created_at) >= new Date(customStartDate))
        }
        if (customEndDate) {
          const endDate = new Date(customEndDate)
          endDate.setHours(23, 59, 59, 999)
          filtered = filtered.filter((s) => new Date(s.created_at) <= endDate)
        }
        break
    }

    return filtered
  }, [shipments, searchQuery, dateFilter, shipmentResultFilter, customStartDate, customEndDate])

  const resultStyles = {
    pending: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
    match: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
    mismatch: 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300',
  }

  const resultLabels = {
    pending: 'Pending',
    match: 'Match',
    mismatch: 'Mismatch',
  }

  const shipmentStatusStyles = {
    in_progress: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300',
    completed: 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300',
    cancelled: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  }

  const shipmentStatusLabels = {
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getPerformer = (verification: VerificationWithUser) => {
    if (verification.user?.full_name) return verification.user.full_name
    if (verification.user?.email) return verification.user.email
    return 'Unknown'
  }

  const getShipmentPerformer = (shipment: ShipmentVerificationWithUser) => {
    if (shipment.user?.full_name) return shipment.user.full_name
    if (shipment.user?.email) return shipment.user.email
    return 'Unknown'
  }

  const openModal = (verification: VerificationWithUser) => {
    setModal({ isOpen: true, verification })
  }

  const closeModal = () => {
    setModal({ isOpen: false, verification: null })
  }

  const openShipmentModal = (shipment: ShipmentVerificationWithUser) => {
    setShipmentModal({ isOpen: true, shipment })
  }

  const closeShipmentModal = () => {
    setShipmentModal({ isOpen: false, shipment: null })
  }

  const downloadPdf = async (
    rowId: string,
    pdfUrl: string | null,
    verificationId: string | null
  ) => {
    if (!pdfUrl) {
      alert('No PDF available for this verification.')
      return
    }

    try {
      // Ask the backend for a fresh presigned GET URL (replaces the former
      // client-side storage signing); falls back to the stored URL on error.
      let downloadUrl = pdfUrl
      const { data, error } = await getVerificationPdfUrl(rowId)
      if (!error && data?.url) {
        downloadUrl = data.url
      } else if (error) {
        console.log('Using stored URL (signed URL not available):', error.message)
      }

      // Use fetch + blob approach to avoid popup blockers
      const response = await fetch(downloadUrl)
      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`)
      }

      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      // Create a download link and click it
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = verificationId ? `${verificationId}.pdf` : 'verification-report.pdf'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error('Error downloading PDF:', err)
      // Last resort: try opening in new tab
      try {
        window.open(pdfUrl, '_blank')
      } catch {
        alert('Failed to download PDF. Please try again or check your popup blocker settings.')
      }
    }
  }

  const clearFilters = () => {
    setSearchQuery('')
    setDateFilter('all')
    setResultFilter('all')
    setShipmentResultFilter('all')
    setCustomStartDate('')
    setCustomEndDate('')
  }

  const hasActiveFilters = activeTab === 'verification'
    ? (searchQuery || dateFilter !== 'all' || resultFilter !== 'all')
    : (searchQuery || dateFilter !== 'all' || shipmentResultFilter !== 'all')

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader title="Verification History" subtitle="View and search past verifications" />

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('verification')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'verification'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <svg className="w-5 h-5 inline-block mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            IP Verification
            {verifications.length > 0 && (
              <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 py-0.5 px-2 rounded-full text-xs">
                {verifications.length}
              </span>
            )}
          </button>
          {/* TODO: Re-enable when shipment reconciliation feature is ready */}
          {/* <button
            onClick={() => setActiveTab('shipment')}
            className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === 'shipment'
                ? 'border-primary-600 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <svg className="w-5 h-5 inline-block mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            Shipment Reconciliation
            {shipments.length > 0 && (
              <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 py-0.5 px-2 rounded-full text-xs">
                {shipments.length}
              </span>
            )}
          </button> */}
        </nav>
      </div>

      <Card>
        <CardHeader>
          <div className="space-y-4">
            {/* Search and Filter Toggle */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder={activeTab === 'verification'
                    ? "Search by IXRS, Vial, or Verification ID..."
                    : "Search by ID or performed by..."}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={showFilters ? 'primary' : 'outline'}
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                  </svg>
                  Filters
                </Button>
                {hasActiveFilters && (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className="p-4 bg-gray-50 dark:bg-gray-900/40 rounded-lg space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Date Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Date Range
                    </label>
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value as DateFilter)}
                      className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">Last 7 Days</option>
                      <option value="month">Last 30 Days</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>

                  {/* Result Filter - different options per tab */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {activeTab === 'verification' ? 'Result' : 'Status'}
                    </label>
                    {activeTab === 'verification' ? (
                      <select
                        value={resultFilter}
                        onChange={(e) => setResultFilter(e.target.value as ResultFilter)}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      >
                        <option value="all">All Results</option>
                        <option value="match">Match Only</option>
                        <option value="mismatch">Mismatch Only</option>
                      </select>
                    ) : (
                      <select
                        value={shipmentResultFilter}
                        onChange={(e) => setShipmentResultFilter(e.target.value as ShipmentResultFilter)}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      >
                        <option value="all">All Status</option>
                        <option value="completed">Completed Only</option>
                        <option value="in_progress">In Progress Only</option>
                      </select>
                    )}
                  </div>
                </div>

                {/* Custom Date Range */}
                {dateFilter === 'custom' && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
            </div>
          ) : activeTab === 'verification' ? (
            /* IP Verification Tab Content */
            filteredVerifications.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">
                  {hasActiveFilters ? 'No verifications match your filters' : 'No verification history found'}
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  {hasActiveFilters ? 'Try adjusting your filters' : 'Completed verifications will appear here'}
                </p>
              </div>
            ) : (
              <>
                {/* Stats Summary */}
                <div className="mb-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>Showing {filteredVerifications.length} verification{filteredVerifications.length !== 1 ? 's' : ''}</span>
                  <div className="flex gap-4">
                    <span className="text-green-600 dark:text-green-400">
                      {filteredVerifications.filter(v => v.match_result === 'match').length} matches
                    </span>
                    <span className="text-red-600 dark:text-red-400">
                      {filteredVerifications.filter(v => v.match_result === 'mismatch').length} mismatches
                    </span>
                  </div>
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Verification ID</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">IXRS Number</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Vial Number</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Result</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Performed By</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredVerifications.map((verification) => (
                        <tr
                          key={verification.id}
                          className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => openModal(verification)}
                        >
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(verification.created_at)}
                          </td>
                          <td className="py-3 px-4 text-sm font-mono text-gray-900 dark:text-gray-100">
                            {verification.verification_id || '-'}
                          </td>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-gray-100">
                            {verification.ixrs_number}
                          </td>
                          <td className="py-3 px-4 text-gray-900 dark:text-gray-100">
                            {verification.vial_number}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${resultStyles[verification.match_result]}`}>
                              {resultLabels[verification.match_result]}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {getPerformer(verification)}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {verification.pdf_url && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  downloadPdf(verification.id, verification.pdf_url, verification.verification_id)
                                }}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredVerifications.map((verification) => (
                    <div
                      key={verification.id}
                      className="py-4 cursor-pointer active:bg-gray-50"
                      onClick={() => openModal(verification)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">IXRS: {verification.ixrs_number}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">Vial: {verification.vial_number}</p>
                        </div>
                        <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${resultStyles[verification.match_result]}`}>
                          {resultLabels[verification.match_result]}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatDate(verification.created_at)}</span>
                        <span>{verification.verification_id || ''}</span>
                      </div>
                      {verification.pdf_url && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 w-full"
                          onClick={(e) => {
                            e.stopPropagation()
                            downloadPdf(verification.id, verification.pdf_url, verification.verification_id)
                          }}
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download PDF
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )
          ) : (
            /* Shipment Reconciliation Tab Content */
            filteredShipments.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                <p className="text-gray-500 dark:text-gray-400">
                  {hasActiveFilters ? 'No shipments match your filters' : 'No shipment reconciliation history found'}
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                  {hasActiveFilters ? 'Try adjusting your filters' : 'Completed shipment reconciliations will appear here'}
                </p>
              </div>
            ) : (
              <>
                {/* Stats Summary */}
                <div className="mb-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>Showing {filteredShipments.length} shipment{filteredShipments.length !== 1 ? 's' : ''}</span>
                  <div className="flex gap-4">
                    <span className="text-green-600 dark:text-green-400">
                      {filteredShipments.filter(s => s.status === 'completed').length} completed
                    </span>
                    <span className="text-yellow-600 dark:text-yellow-400">
                      {filteredShipments.filter(s => s.status === 'in_progress').length} in progress
                    </span>
                  </div>
                </div>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Manifest IDs</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Matched</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Missing</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Extra</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Status</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Performed By</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700 dark:text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredShipments.map((shipment) => (
                        <tr
                          key={shipment.id}
                          className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                          onClick={() => openShipmentModal(shipment)}
                        >
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {formatDate(shipment.created_at)}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900 dark:text-gray-100">
                            {shipment.manifest_ids.length} IDs
                          </td>
                          <td className="py-3 px-4 text-sm text-green-600 dark:text-green-400 font-medium">
                            {shipment.matched_ids.length}
                          </td>
                          <td className="py-3 px-4 text-sm text-red-600 dark:text-red-400 font-medium">
                            {shipment.unmatched_ids.length}
                          </td>
                          <td className="py-3 px-4 text-sm text-yellow-600 dark:text-yellow-400 font-medium">
                            {shipment.extra_ids.length}
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${shipmentStatusStyles[shipment.status]}`}>
                              {shipmentStatusLabels[shipment.status]}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400">
                            {getShipmentPerformer(shipment)}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {/* Actions placeholder */}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredShipments.map((shipment) => (
                    <div
                      key={shipment.id}
                      className="py-4 cursor-pointer active:bg-gray-50"
                      onClick={() => openShipmentModal(shipment)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-medium text-gray-900 dark:text-gray-100">{shipment.manifest_ids.length} Manifest IDs</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            <span className="text-green-600 dark:text-green-400">{shipment.matched_ids.length} matched</span>
                            {shipment.unmatched_ids.length > 0 && (
                              <span className="text-red-600 dark:text-red-400 ml-2">{shipment.unmatched_ids.length} missing</span>
                            )}
                            {shipment.extra_ids.length > 0 && (
                              <span className="text-yellow-600 dark:text-yellow-400 ml-2">{shipment.extra_ids.length} extra</span>
                            )}
                          </p>
                        </div>
                        <span className={`inline-flex items-center whitespace-nowrap px-2.5 py-0.5 rounded-full text-xs font-medium ${shipmentStatusStyles[shipment.status]}`}>
                          {shipmentStatusLabels[shipment.status]}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{formatDate(shipment.created_at)}</span>
                        <span>{getShipmentPerformer(shipment)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      {modal.isOpen && modal.verification && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeModal}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Verification Details</h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Result Badge */}
                <div className="flex justify-center">
                  <span className={`px-4 py-2 rounded-full text-lg font-medium ${resultStyles[modal.verification.match_result]}`}>
                    {resultLabels[modal.verification.match_result]}
                    {modal.verification.match_similarity > 0 && modal.verification.match_similarity < 1 && (
                      <span className="ml-2 text-sm">
                        ({Math.round(modal.verification.match_similarity * 100)}% similarity)
                      </span>
                    )}
                  </span>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Verification ID</p>
                    <p className="font-mono text-gray-900 dark:text-gray-100">{modal.verification.verification_id || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date</p>
                    <p className="text-gray-900 dark:text-gray-100">{formatDate(modal.verification.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">IXRS Number</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{modal.verification.ixrs_number}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Vial Number</p>
                    <p className="font-medium text-gray-900 dark:text-gray-100">{modal.verification.vial_number}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Performed By</p>
                    <p className="text-gray-900 dark:text-gray-100">{getPerformer(modal.verification)}</p>
                  </div>
                  {modal.verification.notes && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Notes</p>
                      <p className="text-gray-900 dark:text-gray-100">{modal.verification.notes}</p>
                    </div>
                  )}
                  {modal.verification.recipient_emails && modal.verification.recipient_emails.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Email Recipients</p>
                      <p className="text-gray-900 dark:text-gray-100">{modal.verification.recipient_emails.join(', ')}</p>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  {modal.verification.pdf_url && (
                    <Button
                      variant="primary"
                      className="flex-1"
                      onClick={() => downloadPdf(modal.verification!.id, modal.verification!.pdf_url, modal.verification!.verification_id)}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Download PDF
                    </Button>
                  )}
                  <Button variant="outline" className="flex-1" onClick={closeModal}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Shipment Detail Modal */}
      {shipmentModal.isOpen && shipmentModal.shipment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeShipmentModal}></div>
            <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Shipment Reconciliation Details</h3>
                <button
                  onClick={closeShipmentModal}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex justify-center">
                  <span className={`px-4 py-2 rounded-full text-lg font-medium ${shipmentStatusStyles[shipmentModal.shipment.status]}`}>
                    {shipmentStatusLabels[shipmentModal.shipment.status]}
                  </span>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-4 pt-4">
                  <div className="text-center p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{shipmentModal.shipment.matched_ids.length}</p>
                    <p className="text-sm text-green-700 dark:text-green-400">Matched</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-red-600 dark:text-red-400">{shipmentModal.shipment.unmatched_ids.length}</p>
                    <p className="text-sm text-red-700 dark:text-red-400">Missing</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{shipmentModal.shipment.extra_ids.length}</p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400">Extra</p>
                  </div>
                </div>

                {/* Details */}
                <div className="grid grid-cols-2 gap-4 pt-4">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Date</p>
                    <p className="text-gray-900 dark:text-gray-100">{formatDate(shipmentModal.shipment.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Performed By</p>
                    <p className="text-gray-900 dark:text-gray-100">{getShipmentPerformer(shipmentModal.shipment)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Manifest IDs</p>
                    <p className="text-gray-900 dark:text-gray-100">{shipmentModal.shipment.manifest_ids.length}</p>
                  </div>
                  {shipmentModal.shipment.completed_at && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Completed At</p>
                      <p className="text-gray-900 dark:text-gray-100">{formatDate(shipmentModal.shipment.completed_at)}</p>
                    </div>
                  )}
                </div>

                {/* ID Lists */}
                {shipmentModal.shipment.unmatched_ids.length > 0 && (
                  <div className="pt-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Missing IDs (on manifest but not scanned)</p>
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {shipmentModal.shipment.unmatched_ids.map((id, i) => (
                          <span key={i} className="px-2 py-1 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300 rounded text-sm font-mono">
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {shipmentModal.shipment.extra_ids.length > 0 && (
                  <div className="pt-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Extra IDs (scanned but not on manifest)</p>
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {shipmentModal.shipment.extra_ids.map((id, i) => (
                          <span key={i} className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300 rounded text-sm font-mono">
                            {id}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {shipmentModal.shipment.notes && (
                  <div className="pt-4">
                    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Notes</p>
                    <p className="text-gray-900 dark:text-gray-100">{shipmentModal.shipment.notes}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button variant="outline" className="flex-1" onClick={closeShipmentModal}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
